import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './ApplicationHistory.css';

const CATEGORY_LABELS = {
  JOINED: "入社申請",
  joined: "入社届",
  VACATION: "有給休暇",
  HOLIDAY_WORK: "休日出勤",
  ADDRESS: "住所変更",
  NAME_CHANGE: "氏名変更",
  BANK: "振込先変更",
  COMMUTE: "交通機関",
  FAMILY: "家族異動",
  REMOVE: "異動年月日",
  RETIREMENT: "退職",
  MYNUMBER: "マイナンバー",
  SPECIAL_LEAVE: "特別休暇申請",
  SERI: "生理休暇",
  OVERTIME: "残業",
  LATE_IN: "遅刻",
  EARLY_OUT: "早退",
  TRANSPORT_ROUND: "日帰り申請",
  ALLOWANCE: "諸手当",
  OTHER: "その他",
};

const STATUS_LABELS = {
  DRAFT: "下書き　",
  SUPERVISOR_PENDING: "上司承認待ち",
  SUPERVISOR_APPROVED: "上司承認済",
  SUBMITTED: "申請中　",
  APPROVED: "承認済み",
  REJECTED: "　却下　",
  RETURNED: "差し戻し",
  CANCELLED: "取り消し",
};

// 大文字小文字の違いを吸収してラベルを引く（未登録はそのまま表示）
const labelFor = (map, key) =>
  map[key] || map[String(key || "").toUpperCase()] || key || "—";

const ApplicationHistory = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/api/employee/applications/');
        setApplications(res.data || []);
      } catch (err) {
        console.error('申請履歴の取得エラー:', err);
        setApplications([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const formatDate = (dt) => {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const getCategoryLabel = (cat) => labelFor(CATEGORY_LABELS, cat);

  return (
    <div className="application-history-container">
      <h3>申請履歴</h3>
      {loading ? (
        <p className="text-muted">読み込み中...</p>
      ) : applications.length === 0 ? (
        <p className="text-muted">申請履歴はありません。</p>
      ) : (
        <div className="application-history-list">
          {applications.map((app) => (
            <div key={app.id} className="application-history-item">
              <div className="application-history-header" onClick={() => setSelectedApp(selectedApp?.id === app.id ? null : app)}>
                <span className="application-history-category">{getCategoryLabel(app.category)}</span>
                <span className={`application-history-status status-${(app.status || '').toLowerCase()}`}>
                  {labelFor(STATUS_LABELS, app.status)}
                </span>
                <span className="application-history-date">{formatDate(app.submitted_at)}</span>
              </div>
              {selectedApp?.id === app.id && (
                <div className="application-history-detail">
                  <ApplicationDetailContent app={app} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="button-group application-history-button-group">
        <button className="btn-back" onClick={() => navigate('/applications')}>戻る</button>
      </div>
    </div>
  );
};

const ApplicationDetailContent = ({ app }) => {
  const fd = app.formData || {};
  const category = app.category;
  const formatYen = (n) => {
    const num = typeof n === 'number' ? n : Number(String(n ?? '').replace(/,/g, ''));
    if (!Number.isFinite(num)) return '—';
    return num.toLocaleString('ja-JP');
  };

  const renderContent = () => {
    switch (category) {
      case 'VACATION':
        const startD = fd.start_date || fd.application_date;
        const endD = fd.end_date || fd.application_date;
        return (
          <dl>
            <dt>期間</dt><dd>{startD && endD ? (startD === endD ? startD : `${startD} ～ ${endD}`) : '—'}</dd>
            <dt>理由</dt><dd>{fd.reason || '—'}</dd>
          </dl>
        );

      case 'HOLIDAY_WORK': {
        const dates = Array.isArray(fd.selectedDates)
          ? fd.selectedDates.join('、')
          : (fd.selectedDates || fd.work_date || '—');
        
        const workTime = (fd.workStartTime && fd.workEndTime)
          ? `${fd.workStartTime} ～ ${fd.workEndTime}`
          : '—';

        return (
          <dl>
            <dt>出勤予定日</dt><dd>{dates}</dd>
            <dt>勤務予定時間</dt><dd>{workTime}</dd>
            <dt>休憩時間</dt><dd>{fd.breakTime || '—'}</dd>
            <dt>理由</dt><dd>{fd.reason || '—'}</dd>
          </dl>
        );
      }
        
      case 'NAME_CHANGE':
        return (
          <dl>
            <dt>旧氏名</dt><dd>{fd.oldName || '—'}</dd>
            <dt>旧氏名（フリガナ）</dt><dd>{fd.oldKanaName || '—'}</dd>
            <dt>新氏名</dt><dd>{fd.newName || '—'}</dd>
            <dt>新氏名（フリガナ）</dt><dd>{fd.newKanaName || '—'}</dd>
          </dl>
        );
      case 'ADDRESS':
        return (
          <dl>
            <dt>旧住所</dt><dd>{fd.oldAddress || '—'}</dd>
            <dt>新住所</dt><dd>{fd.newAddress || '—'}</dd>
          </dl>
        );
      case 'BANK':
        return (
          <dl>
            <dt>銀行名</dt><dd>{fd.bankName || '—'}</dd>
            <dt>支店名</dt><dd>{fd.branchName || '—'}</dd>
            <dt>口座番号</dt><dd>{fd.accountNumber || '—'}</dd>
          </dl>
        );
      case 'COMMUTE':
        const routes = fd.routes || [];
        return (
          <dl>
            <dt>路線</dt>
            <dd>
              {routes.length > 0 ? routes.map((r, i) => (
                <div key={i}>
                  {r.route} {r.stationFrom}～{r.stationTo} {formatYen(r.fare)}円
                </div>
              )) : '—'}
            </dd>
          </dl>
        );
      case 'REMOVE':
        return (
          <dl>
            <dt>異動年月日</dt><dd>{fd.application_date || fd.start_date || '—'}</dd>
            <dt>理由</dt><dd>{fd.reason || '—'}</dd>
          </dl>
        );
      case 'RETIREMENT':
        return (
          <dl>
            <dt>入社日</dt><dd>{fd.start_date || '—'}</dd>
            <dt>退職日</dt><dd>{fd.end_date || '—'}</dd>
            <dt>理由</dt><dd>{fd.reason || '—'}</dd>
          </dl>
        );
      case 'JOINED':
      case 'joined':
        return (
          <dl>
            <dt>入社日</dt><dd>{fd.startDate || fd.start_date || '—'}</dd>
            <dt>氏名</dt><dd>{fd.name || '—'}</dd>
            <dt>住所</dt><dd>{fd.address || '—'}</dd>
          </dl>
        );
      case 'FAMILY':
        return (
          <dl>
            <dt>異動区分</dt><dd>{fd.changeType || '—'}</dd>
            <dt>家族数</dt><dd>{fd.familyMembersCount ?? '—'}</dd>
          </dl>
        );
      case 'SPECIAL_LEAVE':
        return (
          <dl>
            <dt>開始日</dt><dd>{fd.start_date || '—'}</dd>
            <dt>終了日</dt><dd>{fd.end_date || '—'}</dd>
            <dt>理由</dt><dd>{fd.reason || '—'}</dd>
          </dl>
        );
      case 'TRANSPORT_ROUND': {
        const trRoutes = Array.isArray(fd.routes) ? fd.routes : [];
        return (
          <dl>
            <dt>日付</dt><dd>{fd.targetDate || '—'}</dd>
            <dt>出張用件</dt><dd>{fd.businessPurpose || '—'}</dd>
            <dt>出張時間</dt><dd>{fd.businessStartTime && fd.businessEndTime ? `${fd.businessStartTime} ～ ${fd.businessEndTime}` : (fd.businessStartTime || fd.businessEndTime || '—')}</dd>
            <dt>利用交通機関</dt>
            <dd>
              {trRoutes.length > 0 ? trRoutes.map((r, i) => (
                <div key={i}>
                  {r.transportName} {r.stationFrom}～{r.stationTo} {formatYen(r.fare)}円{r.roundTrip ? '（往復）' : ''}
                </div>
              )) : (fd.trainName || '—')}
            </dd>
            <dt>金額</dt><dd>{fd.amount ? `${formatYen(fd.amount)}円` : '—'}</dd>
          </dl>
        );
      }
      case 'ALLOWANCE': {
        const allowances = [
          ['家族手当', fd.familyAllowance],
          ['住宅手当', fd.housingAllowance],
          ['派遣手当', fd.dispatchAllowance],
          ['都市手当', fd.cityAllowance],
          ['資格手当', fd.qualificationAllowance],
        ].filter(([, v]) => Number(v) > 0);
        return (
          <dl>
            {allowances.length > 0 ? allowances.map(([label, v]) => (
              <React.Fragment key={label}>
                <dt>{label}</dt><dd>{formatYen(v)}円</dd>
              </React.Fragment>
            )) : (<><dt>手当</dt><dd>—</dd></>)}
            <dt>合計</dt><dd>{formatYen(fd.totalAllowance || 0)}円</dd>
          </dl>
        );
      }
      default:
        return (
          <pre className="application-detail-json">{JSON.stringify(fd, null, 2)}</pre>
        );
    }
  };

  return (
    <div className="application-detail-content">
      {renderContent()}
      {app.admin_comment && (
        <div className="admin-comment">
          <strong>管理者コメント</strong>: {app.admin_comment}
        </div>
      )}
    </div>
  );
};

export default ApplicationHistory;
